import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { clearAllTableSessionState } from "../lib/tableMemory";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not logged in
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // UI-STABILITY-1B-tier1: backend logout call may fail (offline,
      // already-expired session). We still proceed with local cleanup so
      // the user is logged out client-side. Log so the issue is not
      // completely silent during debugging.
      // eslint-disable-next-line no-console
      console.warn("Backend logout call failed; clearing local session anyway:", err?.message);
    }
    // UI-STABILITY-1B: clear ephemeral table search state on explicit
    // logout so the next user (or the same user logging back in) does not
    // inherit the previous search text. Persistent listPrefs (localStorage)
    // are intentionally left untouched.
    clearAllTableSessionState();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
